import { z } from "zod";
export const printMarksSchema = z
  .object({
    crop: z.boolean(),
    lengthMm: z.number().finite().min(1).max(20),
    offsetMm: z.number().finite().min(0.5).max(20),
  })
  .strict();
export type PrintMarks = z.infer<typeof printMarksSchema>;
export const CROP_MARK_STROKE_PT = 0.25;
type Rect = { x: number; y: number; width: number; height: number };
/** Top-left physical coordinates. Marks align with trim but stay outside bleed. */
export function resolveCropMarks(
  boxes: { media: Rect; trim: Rect; bleed: Rect },
  raw: PrintMarks | undefined,
) {
  if (raw === undefined) return [];
  const marks = printMarksSchema.parse(raw);
  if (!marks.crop) return [];
  const { media, trim, bleed } = boxes,
    { lengthMm: length, offsetMm: offset } = marks;
  const lines: {
    start: { x: number; y: number };
    end: { x: number; y: number };
  }[] = [];
  for (const y of [trim.y, trim.y + trim.height]) {
    lines.push({
      start: { x: bleed.x - offset - length, y },
      end: { x: bleed.x - offset, y },
    });
    lines.push({
      start: { x: bleed.x + bleed.width + offset, y },
      end: { x: bleed.x + bleed.width + offset + length, y },
    });
  }
  for (const x of [trim.x, trim.x + trim.width]) {
    lines.push({
      start: { x, y: bleed.y - offset - length },
      end: { x, y: bleed.y - offset },
    });
    lines.push({
      start: { x, y: bleed.y + bleed.height + offset },
      end: { x, y: bleed.y + bleed.height + offset + length },
    });
  }
  const halfStrokeMm = (CROP_MARK_STROKE_PT * 25.4) / 144;
  for (const line of lines)
    for (const p of [line.start, line.end]) {
      if (
        ![p.x, p.y].every(Number.isFinite) ||
        p.x - halfStrokeMm < media.x ||
        p.y - halfStrokeMm < media.y ||
        p.x + halfStrokeMm > media.x + media.width ||
        p.y + halfStrokeMm > media.y + media.height
      )
        throw new Error(
          "Crop marks need more slug space on every edge; increase slug or reduce mark length/offset",
        );
    }
  return lines;
}
export function validatePrintMarksGeometry(
  geometry: {
    trim: { width: number; height: number };
    bleed: { top: number; right: number; bottom: number; left: number };
    slug: { top: number; right: number; bottom: number; left: number };
  },
  marks: PrintMarks | undefined,
) {
  const { trim, bleed, slug } = geometry;
  resolveCropMarks(
    {
      media: {
        x: 0,
        y: 0,
        width: slug.left + bleed.left + trim.width + bleed.right + slug.right,
        height: slug.top + bleed.top + trim.height + bleed.bottom + slug.bottom,
      },
      trim: { x: slug.left + bleed.left, y: slug.top + bleed.top, ...trim },
      bleed: {
        x: slug.left,
        y: slug.top,
        width: trim.width + bleed.left + bleed.right,
        height: trim.height + bleed.top + bleed.bottom,
      },
    },
    marks,
  );
}
